import { isCalendarDayString } from './datetime';
import { MAX_WEIGHT_GOAL_KG, MIN_WEIGHT_GOAL_KG, WEIGHT_GOAL_KG_VALUES } from './healthWeight';

export const KCAL_PER_KG = 7700;

export const MIN_AGE_YEARS = 14;
export const MAX_AGE_YEARS = 90;

export const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
export const MONTH_VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;
export const MIN_HEIGHT_CM = 120;
export const MAX_HEIGHT_CM = 220;
export const MIN_BODY_WEIGHT_KG = 35;
export const MAX_BODY_WEIGHT_KG = 160;
export const MIN_WIZARD_KCAL = 800;
export const MAX_WIZARD_KCAL = 6000;
export const WIZARD_KCAL_STEP = 50;

export enum HealthSex {
    MALE = 'MALE',
    FEMALE = 'FEMALE',
}

export enum HealthActivityLevel {
    SEDENTARY = 'SEDENTARY',
    LIGHT = 'LIGHT',
    MODERATE = 'MODERATE',
    ACTIVE = 'ACTIVE',
    ATHLETE = 'ATHLETE',
}

export enum HealthWeightIntent {
    LOSE = 'LOSE',
    MAINTAIN = 'MAINTAIN',
    GAIN = 'GAIN',
}

export type CalorieGoalSetupInput = {
    sex: HealthSex;
    birthDate: string;
    heightCm: number;
    activityLevel: HealthActivityLevel;
    weightIntent: HealthWeightIntent;
    rateKgPerWeek?: number | null;
    bodyFatPercent?: number | null;
};

export type CalorieBodyComposition = {
    leanMassKg?: number | null;
    bodyFatPercent?: number | null;
    bmrKcal?: number | null;
};

export function calorieBodyCompositionFromProfile(
    profile:
        | {
              latestLeanMassKg?: number | null;
              latestBodyFatPercent?: number | null;
              latestBmrKcal?: number | null;
          }
        | null
        | undefined,
): CalorieBodyComposition | null {
    const leanMassKg = profile?.latestLeanMassKg;
    if (leanMassKg == null || !Number.isFinite(leanMassKg) || leanMassKg <= 0) return null;
    return {
        leanMassKg,
        bodyFatPercent: profile?.latestBodyFatPercent ?? null,
        bmrKcal: profile?.latestBmrKcal ?? null,
    };
}

export function calcBmrKatchMcArdle(leanMassKg: number): number {
    return 370 + 21.6 * leanMassKg;
}

export function resolveBmr(
    setup: Pick<CalorieGoalSetupInput, 'sex' | 'birthDate' | 'heightCm'>,
    bodyWeightKg: number,
    composition?: CalorieBodyComposition | null,
): number {
    const leanMassKg = composition?.leanMassKg;
    if (leanMassKg != null && Number.isFinite(leanMassKg) && leanMassKg > 0) {
        return calcBmrKatchMcArdle(leanMassKg);
    }
    return calcBmr(setup, bodyWeightKg);
}

const ACTIVITY_MULTIPLIERS: Record<HealthActivityLevel, number> = {
    [HealthActivityLevel.SEDENTARY]: 1.2,
    [HealthActivityLevel.LIGHT]: 1.375,
    [HealthActivityLevel.MODERATE]: 1.55,
    [HealthActivityLevel.ACTIVE]: 1.725,
    [HealthActivityLevel.ATHLETE]: 1.9,
};

export function daysInMonth(year: number, month: number): number {
    return new Date(year, month, 0).getDate();
}

export function birthDateFromParts(year: number, month: number, day: number): string {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function parseBirthDate(birthDate: string): { year: number; month: number; day: number } | null {
    if (!isCalendarDayString(birthDate)) return null;
    const [y, m, d] = birthDate.split('-').map(Number);
    if (!y || !m || !d || m < 1 || m > 12 || d < 1 || d > daysInMonth(y, m)) return null;
    return { year: y, month: m, day: d };
}

export function ageYearsFromBirthDate(birthDate: string, asOf: Date = new Date()): number {
    const parsed = parseBirthDate(birthDate);
    if (!parsed) return 0;
    let age = asOf.getFullYear() - parsed.year;
    const month = asOf.getMonth() + 1;
    if (month < parsed.month || (month === parsed.month && asOf.getDate() < parsed.day)) age -= 1;
    return age;
}

export function formatBirthDateDisplay(birthDate: string, opts?: { monthLabels?: readonly string[]; order?: 'MDY' | 'DMY' }): string {
    const parsed = parseBirthDate(birthDate);
    if (!parsed) return '—';
    const labels = opts?.monthLabels ?? MONTH_LABELS;
    const month = labels[parsed.month - 1];
    if (opts?.order === 'DMY') return `${parsed.day} ${month} ${parsed.year}`;
    return `${month} ${parsed.day}, ${parsed.year}`;
}

export function formatBirthDateWithAge(
    birthDate: string,
    asOf?: Date,
    opts?: { monthLabels?: readonly string[]; order?: 'MDY' | 'DMY'; ageSuffix?: string },
): string {
    return `${formatBirthDateDisplay(birthDate, opts)} (${ageYearsFromBirthDate(birthDate, asOf)} ${opts?.ageSuffix ?? 'yrs'})`;
}

export function defaultBirthDateForAge(ageYears: number, asOf: Date = new Date()): string {
    return birthDateFromParts(asOf.getFullYear() - ageYears, 1, 1);
}

export function birthDayValues(year: number, month: number): number[] {
    return rangeValues(1, daysInMonth(year, month));
}

export function clampBirthDateParts(year: number, month: number, day: number): { year: number; month: number; day: number } {
    const maxDay = daysInMonth(year, month);
    return { year, month, day: Math.min(day, maxDay) };
}

export function birthYearValues(asOf: Date = new Date()): number[] {
    const maxYear = asOf.getFullYear() - MIN_AGE_YEARS;
    const minYear = asOf.getFullYear() - MAX_AGE_YEARS;
    return rangeValues(minYear, maxYear);
}

export function calcBmr(setup: Pick<CalorieGoalSetupInput, 'sex' | 'birthDate' | 'heightCm'>, bodyWeightKg: number): number {
    const { sex, birthDate, heightCm } = setup;
    const ageYears = ageYearsFromBirthDate(birthDate);
    if (sex === HealthSex.MALE) {
        return 10 * bodyWeightKg + 6.25 * heightCm - 5 * ageYears + 5;
    }
    return 10 * bodyWeightKg + 6.25 * heightCm - 5 * ageYears - 161;
}

export function tdeeFor(setup: CalorieGoalSetupInput, bodyWeightKg: number, composition?: CalorieBodyComposition | null): number {
    const bmr = resolveBmr(setup, bodyWeightKg, composition);
    return bmr * ACTIVITY_MULTIPLIERS[setup.activityLevel];
}

export function calcDailyCalorieGoal(
    setup: CalorieGoalSetupInput,
    bodyWeightKg: number,
    composition?: CalorieBodyComposition | null,
): number {
    let tdee = tdeeFor(setup, bodyWeightKg, composition);
    const rate = setup.rateKgPerWeek ?? 0;
    if (setup.weightIntent === HealthWeightIntent.LOSE) {
        tdee -= (rate * KCAL_PER_KG) / 7;
    } else if (setup.weightIntent === HealthWeightIntent.GAIN) {
        tdee += (rate * KCAL_PER_KG) / 7;
    }
    return Math.round(tdee / WIZARD_KCAL_STEP) * WIZARD_KCAL_STEP;
}

export function impliedRateKgPerWeek(
    setup: CalorieGoalSetupInput,
    calories: number,
    bodyWeightKg: number,
    composition?: CalorieBodyComposition | null,
): number {
    const tdee = tdeeFor(setup, bodyWeightKg, composition);
    const dailyDiff = calories - tdee;
    const kgPerWeek = (dailyDiff * 7) / KCAL_PER_KG;
    return Math.round(kgPerWeek * 100) / 100;
}

export function needsRateStep(intent: HealthWeightIntent): boolean {
    return intent === HealthWeightIntent.LOSE || intent === HealthWeightIntent.GAIN;
}

export function tracksWeightGoal(intent: HealthWeightIntent): boolean {
    return needsRateStep(intent) || intent === HealthWeightIntent.MAINTAIN;
}

export function seedWeightGoalKg(bodyWeightKg: number, intent: HealthWeightIntent): number {
    if (intent === HealthWeightIntent.MAINTAIN) {
        return Math.max(MIN_WEIGHT_GOAL_KG, Math.min(MAX_WEIGHT_GOAL_KG, snapCalorieBodyWeightKg(bodyWeightKg)));
    }
    const seed =
        intent === HealthWeightIntent.LOSE
            ? Math.max(MIN_WEIGHT_GOAL_KG, bodyWeightKg - 5)
            : Math.min(MAX_WEIGHT_GOAL_KG, bodyWeightKg + 5);
    return Math.round(seed * 2) / 2;
}

export function resolveWeightGoalKgForIntent(
    intent: HealthWeightIntent,
    bodyWeightKg: number,
    currentGoalKg: number | null,
): number | null {
    if (!tracksWeightGoal(intent)) return null;
    if (currentGoalKg != null) return currentGoalKg;
    return seedWeightGoalKg(bodyWeightKg, intent);
}

export type CalorieGoalSetupValidation = { ok: true; setup: CalorieGoalSetupInput } | { ok: false; message: string };

export type CalorieBodyWeightValidation = { ok: true; kg: number } | { ok: false; message: string };

function nearestWheelValue(values: readonly number[], value: number): number {
    if (values.length === 0) return value;
    return values.reduce((best, v) => (Math.abs(v - value) < Math.abs(best - value) ? v : best), values[0]!);
}

export function snapCalorieBodyWeightKg(latestWeightKg: unknown): number {
    const parsed = parseCalorieBodyWeightKg(latestWeightKg);
    if (parsed != null) return parsed;
    const n = typeof latestWeightKg === 'number' ? latestWeightKg : latestWeightKg == null ? NaN : Number(latestWeightKg);
    if (Number.isFinite(n)) return nearestWheelValue(WEIGHT_VALUES, n);
    return DEFAULT_BODY_WEIGHT_KG;
}

export function parseCalorieBodyWeightKg(kg: unknown): number | null {
    const n = typeof kg === 'number' ? kg : kg == null ? NaN : Number(kg);
    if (!Number.isFinite(n) || n < MIN_BODY_WEIGHT_KG || n > MAX_BODY_WEIGHT_KG) return null;
    return n;
}

export function validateCalorieBodyWeightKg(kg: unknown): CalorieBodyWeightValidation {
    const parsed = parseCalorieBodyWeightKg(kg);
    if (parsed == null) {
        return { ok: false, message: `Weight must be between ${MIN_BODY_WEIGHT_KG} and ${MAX_BODY_WEIGHT_KG} kg` };
    }
    return { ok: true, kg: +parsed.toFixed(1) };
}

export function validateCalorieGoalSetup(input: Partial<CalorieGoalSetupInput> | null | undefined): CalorieGoalSetupValidation {
    if (!input?.sex || !Object.values(HealthSex).includes(input.sex)) {
        return { ok: false, message: 'Select your sex' };
    }
    const birthDate = input.birthDate;
    if (!birthDate || !parseBirthDate(birthDate)) {
        return { ok: false, message: 'Select your birthday' };
    }
    const ageYears = ageYearsFromBirthDate(birthDate);
    if (ageYears < MIN_AGE_YEARS || ageYears > MAX_AGE_YEARS) {
        return { ok: false, message: `Age must be between ${MIN_AGE_YEARS} and ${MAX_AGE_YEARS}` };
    }
    const heightCm = input.heightCm;
    if (heightCm == null || !Number.isFinite(heightCm) || heightCm < MIN_HEIGHT_CM || heightCm > MAX_HEIGHT_CM) {
        return { ok: false, message: `Height must be between ${MIN_HEIGHT_CM} and ${MAX_HEIGHT_CM} cm` };
    }
    if (!input.activityLevel || !Object.values(HealthActivityLevel).includes(input.activityLevel)) {
        return { ok: false, message: 'Select your activity level' };
    }
    if (!input.weightIntent || !Object.values(HealthWeightIntent).includes(input.weightIntent)) {
        return { ok: false, message: 'Select your goal' };
    }
    const rateKgPerWeek = input.rateKgPerWeek;
    if (needsRateStep(input.weightIntent)) {
        if (rateKgPerWeek == null || !Number.isFinite(rateKgPerWeek) || rateKgPerWeek <= 0 || rateKgPerWeek > 1.5) {
            return { ok: false, message: 'Select a valid weekly rate' };
        }
    }
    return {
        ok: true,
        setup: {
            sex: input.sex,
            birthDate,
            heightCm: Math.round(heightCm),
            activityLevel: input.activityLevel,
            weightIntent: input.weightIntent,
            rateKgPerWeek: needsRateStep(input.weightIntent) ? rateKgPerWeek ?? null : null,
        },
    };
}

export function rangeValues(min: number, max: number, step = 1): number[] {
    const out: number[] = [];
    for (let v = min; v <= max; v += step) out.push(+v.toFixed(10));
    return out;
}

export const HEIGHT_VALUES = rangeValues(MIN_HEIGHT_CM, MAX_HEIGHT_CM);
export const WEIGHT_VALUES = rangeValues(MIN_BODY_WEIGHT_KG, MAX_BODY_WEIGHT_KG);
export const TARGET_WEIGHT_VALUES = WEIGHT_GOAL_KG_VALUES;
export const KCAL_WHEEL_VALUES = rangeValues(MIN_WIZARD_KCAL, MAX_WIZARD_KCAL, WIZARD_KCAL_STEP);

export const LOSE_RATES = [
    { v: 0.25, label: '0.25 kg / week', sub: 'Slow & steady' },
    { v: 0.5, label: '0.5 kg / week', sub: 'Recommended' },
    { v: 0.75, label: '0.75 kg / week', sub: 'Faster' },
    { v: 1.0, label: '1.0 kg / week', sub: 'Aggressive' },
] as const;

export const GAIN_RATES = [
    { v: 0.25, label: '0.25 kg / week', sub: 'Lean & slow' },
    { v: 0.5, label: '0.5 kg / week', sub: 'Recommended' },
    { v: 0.75, label: '0.75 kg / week', sub: 'Faster' },
] as const;

export const ACTIVITY_OPTS = [
    { id: HealthActivityLevel.SEDENTARY, label: 'Sedentary', sub: 'Little or no exercise' },
    { id: HealthActivityLevel.LIGHT, label: 'Light', sub: '1–3 days a week' },
    { id: HealthActivityLevel.MODERATE, label: 'Moderate', sub: '3–5 days a week' },
    { id: HealthActivityLevel.ACTIVE, label: 'Active', sub: '6–7 days a week' },
    { id: HealthActivityLevel.ATHLETE, label: 'Athlete', sub: 'Training twice a day' },
] as const;

export const GOAL_OPTS = [
    { id: HealthWeightIntent.LOSE, label: 'Lose weight' },
    { id: HealthWeightIntent.MAINTAIN, label: 'Maintain weight', sub: 'Stay around your TDEE' },
    { id: HealthWeightIntent.GAIN, label: 'Gain weight' },
] as const;

export const DEFAULT_BODY_WEIGHT_KG = 78;

export function coerceCalorieBodyWeightKg(kg: unknown): number {
    return parseCalorieBodyWeightKg(kg) ?? DEFAULT_BODY_WEIGHT_KG;
}

export const DEFAULT_CALORIE_GOAL_SETUP: CalorieGoalSetupInput = {
    sex: HealthSex.MALE,
    birthDate: defaultBirthDateForAge(30),
    heightCm: 178,
    activityLevel: HealthActivityLevel.MODERATE,
    weightIntent: HealthWeightIntent.MAINTAIN,
    rateKgPerWeek: 0.5,
};
